import { FontWeightType } from 'components/VisualStudioPlayground/VisualEditor/Types'
import FontFaceObserver from 'fontfaceobserver'
import { makeAutoObservable, observable } from 'mobx'
import opentype from 'opentype.js'
import { IFont, LoadingState } from 'stores/IFont'
import { reportError } from 'utils/error'

export type IGoogleFont = {
  family: string
  variants: Array<string>
  files: Record<string, string>
  styles?: string
}

const FONT_LOADING_TIMEOUT = 30000 // 30s

export class GoogleFont implements IFont {
  name: IFont['name']
  family: IFont['family']
  variants: IFont['variants']
  loadingState: IFont['loadingState'] = 'idle'
  loadingPromise?: IFont['loadingPromise']
  files: Record<string, string>
  styles?: string

  analyses = observable.map<string, opentype.Font>()

  constructor(font: IGoogleFont) {
    makeAutoObservable(this)

    this.name = font.family
    this.family = font.family
    this.variants = font.variants
    this.files = font.files
    this.styles = font.styles
  }

  get romanVariants() {
    return this.variants
      .filter((variant) => !variant.includes('italic'))
      .map((variant) => variant.replace('regular', '400'))
      .sort()
  }

  get italicVariants() {
    return this.variants
      .filter((variant) => variant.includes('italic'))
      .map((variant) => {
        if (variant === 'italic') {
          return '400'
        }

        return variant.replace('italic', '')
      })
      .sort()
  }

  get italicWeights() {
    return this.italicVariants.map((variant) => parseInt(variant) as FontWeightType)
  }

  get weights() {
    return this.romanVariants.map((variant) => parseInt(variant) as FontWeightType)
  }

  get url() {
    if (this.styles) {
      return this.styles
    }

    const family = this.family.replace(/ /g, '+')
    let axis: Array<{ prefix: string; range: Array<string> }> = []

    if (this.italicVariants.length > 0) {
      axis.push({ prefix: 'ital', range: this.italicVariants })
    }
    if (this.romanVariants.length > 0) {
      axis.push({ prefix: 'wght', range: this.romanVariants })
    }

    let url = `https://fonts.googleapis.com/css2?family=${family}`

    if (axis.length > 0) {
      url += ':'
      url += axis
        .map((a) => a.prefix)
        .sort()
        .join(',')
      url += '@'
      url += axis.map((a, i) => a.range.map((w) => (axis.length > 1 ? `${i},${w}` : w)).join(';')).join(';')
    }

    url += '&display=swap'

    return url
  }

  setLoadingState = (loadingState: LoadingState) => {
    this.loadingState = loadingState
  }

  load = async () => {
    if (this.loadingState === 'loaded' || this.loadingState === 'loading') {
      return this.loadingPromise
    }

    this.setLoadingState('loading')
    const testString = this.family === 'Noto Color Emoji' ? '😀' : null
    const romanVariants = this.romanVariants.map((variant) =>
      new FontFaceObserver(this.family, { weight: parseInt(variant) }).load(testString, FONT_LOADING_TIMEOUT),
    )
    const italicVariants = this.italicVariants.map((variant) =>
      new FontFaceObserver(this.family, { weight: parseInt(variant), style: 'italic' }).load(
        testString,
        FONT_LOADING_TIMEOUT,
      ),
    )
    this.loadingPromise = Promise.all([...romanVariants, ...italicVariants])
      .then(() => this.setLoadingState('loaded'))
      .catch((e) => {
        reportError(e)
        this.setLoadingState('idle')
      })
    return this.loadingPromise
  }

  analyze = async ({ weight, italic }: { weight: number; italic: boolean }) => {
    const fileKey = this.getFileKey({ weight, italic })
    const fileUrl = this.files[fileKey]
    if (this.analyses.has(fileUrl)) {
      return this.analyses.get(fileUrl)
    }
    const analysis = await opentype.load(this.files[fileKey])
    this.analyses.set(fileUrl, analysis)
    return analysis
  }

  getFileKey = ({ weight, italic }: { weight: number; italic: boolean }) => {
    const hasItalic = this.italicVariants.length > 0
    if (italic && hasItalic && weight === 400) {
      return 'italic'
    }

    if (weight === 400) {
      return 'regular'
    }

    return `${weight}${italic && hasItalic ? 'italic' : ''}`
  }
}
